// Persistent left sidebar — the single home for brand, key stats, filters,
// and the events list. Replaces the previous scatter of floating pills.
//
// Mobile (<sm): the body collapses behind a hamburger toggle so the map
// stays visible. The header strip remains always-on. Expanded mobile state
// fills the available viewport width minus inset.
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FilterState } from '../lib/outbreaks';
import { aggregateStats } from '../lib/outbreaks';
import type { ViewMode } from '../lib/url-state';
import { filtersAreDefault, emptyFilters } from '../lib/url-state';
import { FilterDrawer } from './filters';
import { EventList } from './event-list';
import { ViewToggle } from './view-toggle';
import { CHOROPLETH_LEGEND } from './outbreak-map';
import { LanguageSwitcher } from './language-switcher';
import type { OutbreakWithSources } from '../types/supabase';

interface Props {
  outbreaks: OutbreakWithSources[];
  visible: OutbreakWithSources[];
  filters: FilterState;
  onFilters: (next: FilterState) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  viewMode: ViewMode;
  onViewMode: (next: ViewMode) => void;
  /** Outbreak data still fetching — drives the events-list skeleton. */
  loading?: boolean;
}

export function Sidebar({
  outbreaks, visible, filters, onFilters, selectedId, onSelect,
  viewMode, onViewMode, loading,
}: Props) {
  const { t } = useTranslation();
  // Stats reflect the currently-visible (filtered) slice — events / cases /
  // deaths recompute as the user adjusts timeline, pathogen, syndrome, or
  // confidence filters. When no filter is dirty, these collapse to the
  // lifetime totals.
  const visibleStats = useMemo(() => aggregateStats(visible), [visible]);
  const isFiltered = visible.length !== outbreaks.length;

  // Mobile: body collapsed by default so the map dominates. Desktop ignores
  // this state and always renders the body via sm: breakpoint.
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <aside
      className="pointer-events-auto flex w-[calc(100vw-32px)] max-w-[400px] flex-col overflow-hidden rounded-2xl border border-white/60 bg-white backdrop-blur-xl sm:w-[320px]"
      style={{
        maxHeight: 'calc(100vh - 32px)',
        boxShadow:
          '0 0 0 1px rgba(0,0,0,0.06), 0 1px 0 rgba(255,255,255,0.9) inset, 0 24px 48px -8px rgba(0,0,0,0.32), 0 8px 16px -4px rgba(0,0,0,0.18)',
      }}
    >
      {/* ============================================================
       * Header — wordmark + active/freshness + mobile toggle
       * ============================================================ */}
      <header data-tour="brand" className="flex items-center justify-between border-b border-[var(--color-line)] px-3 py-2 sm:px-4 sm:py-3">
        <a href="/" className="group inline-flex items-center gap-2">
          <Logo />
          <span className="text-[18px] font-semibold tracking-tight text-[var(--color-text)] group-hover:text-[var(--color-accent)]">
            hantavirusmap
            <span className="text-[var(--color-text-3)]">.io</span>
          </span>
        </a>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMobileOpen(o => !o)}
            aria-label={mobileOpen ? t('sidebar.collapse_panel') : t('sidebar.expand_panel')}
            aria-expanded={mobileOpen}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-[var(--color-text-3)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)] sm:hidden"
          >
            {mobileOpen
              ? <ChevronUp size={20} strokeWidth={2.25} />
              : <ChevronDown size={20} strokeWidth={2.25} />}
          </button>
        </div>
      </header>

      <div className={(mobileOpen ? 'flex' : 'hidden') + ' flex-col overflow-hidden sm:flex'}>

      {/* ============================================================
       * Stats — three big numbers
       * ============================================================ */}
      <section data-tour="stats" className="flex items-start justify-start gap-8 border-b border-[var(--color-line)] px-4 py-3">
        <Stat
          value={formatBig(visibleStats.cases)}
          label={t('sidebar.cases_label')}
          tone={isFiltered ? 'accent' : undefined}
        />
        <Stat
          value={visibleStats.deaths.toLocaleString()}
          label={t('sidebar.deaths_label')}
          tone={visibleStats.deaths > 0 ? 'signal' : undefined}
        />
        <Stat
          value={visibleStats.events.toLocaleString()}
          label={t('sidebar.events_label')}
          tone={isFiltered ? 'accent' : undefined}
        />
      </section>

      {/* ============================================================
       * Events list — internal scroll keeps the card compact
       * ============================================================ */}
      <section data-tour="events-list" className="flex min-h-0 flex-1 flex-col border-b border-[var(--color-line)]">
        <EventList
          outbreaks={visible}
          selectedId={selectedId}
          onSelect={onSelect}
          loading={loading}
          inline
        />
      </section>

      {/* ============================================================
       * Filters — compact inline row
       * ============================================================ */}
      <section data-tour="filters" className="hidden px-4 pt-3 pb-2.5 sm:block">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-[8px] font-semibold uppercase tracking-wide text-[var(--color-text-3)]">
            Filters
          </h3>
          {!filtersAreDefault(filters) && (
            <button
              type="button"
              onClick={() => onFilters(emptyFilters())}
              className="flex items-center gap-1 rounded-md bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-600 hover:bg-red-100 hover:text-red-700"
            >
              <X size={10} strokeWidth={2.5} /> Clear
            </button>
          )}
        </div>
        <FilterDrawer
          filters={filters}
          onChange={onFilters}
          visibleCount={visible.length}
          totalCount={outbreaks.length}
          inline
        />
        {isFiltered && (
          <p className="mt-2 text-[10.5px] font-medium uppercase tracking-wide text-[var(--color-accent)]">
            {t('sidebar.filtered_summary', { visible: visible.length, total: outbreaks.length })}
          </p>
        )}
      </section>

      {/* ============================================================
       * Footer
       * ============================================================ */}
      <footer className="flex flex-col gap-2 px-3 py-3 text-[12px]">
        {/* Map views — hidden on mobile to keep the panel compact. */}
        <div className="hidden flex-col gap-2 sm:flex">
          <SectionHeading>Map views</SectionHeading>
          <div data-tour="view-toggle">
            <ViewToggle value={viewMode} onChange={onViewMode} inline />
          </div>
          {viewMode === 'choropleth' && (
            <ul className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1.5" aria-label="Country shading legend">
              {CHOROPLETH_LEGEND.map(item => (
                <li key={item.label} className="flex items-center gap-1.5 text-[11px] text-[var(--color-text-2)]">
                  <span
                    className="h-3 w-3 shrink-0 rounded-sm ring-1 ring-inset ring-black/10"
                    style={{ background: item.color }}
                  />
                  {item.label}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <LanguageSwitcher compact />
          <span className="mono text-[10px] text-[var(--color-text-4)]">CC-BY-4.0</span>
        </div>
      </footer>
      </div>
    </aside>
  );
}

/** Section label, matched to the "Recent events" heading: tiny, greyed,
 *  all-caps. Used to title each sidebar section consistently. */
function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[8px] font-semibold uppercase tracking-wide text-[var(--color-text-3)]">
      {children}
    </h3>
  );
}

function Stat({
  value, label, tone,
}: { value: string; label: string; tone?: 'signal' | 'accent' }) {
  const valueColor =
    tone === 'signal'
      ? 'text-[var(--color-signal)]'
      : tone === 'accent'
      ? 'text-[var(--color-accent)]'
      : 'text-[var(--color-text)]';
  return (
    <div>
      <div className={`text-[18px] font-semibold leading-tight tabular-nums ${valueColor}`}>
        {value}
      </div>
      <div className="mt-0.5 text-[10.5px] font-medium uppercase tracking-wide text-[var(--color-text-3)]">
        {label}
      </div>
    </div>
  );
}


/** Hantavirus virion brand mark — matches /favicon.svg, /apple-touch-icon.svg,
 *  /logo.svg, /og-default.svg. Spike-coated lipid envelope with three
 *  internal RNPs (S, M, L). Built from published structural references
 *  (Battisti 2011, Li 2016, Hepojoki 2012, ViralZone 7079). */
function Logo() {
  return (
    <svg
      viewBox="0 0 64 64"
      className="h-9 w-9 shrink-0"
      aria-hidden
      focusable="false"
    >
      <defs>
        <radialGradient id="hm-env-sb" cx="38%" cy="32%" r="70%">
          <stop offset="0%" stopColor="#aaa3f3" />
          <stop offset="55%" stopColor="#5247e5" />
          <stop offset="100%" stopColor="#312e81" />
        </radialGradient>
        <g id="hm-spike-sb">
          <rect x="-1" y="-21.5" width="2" height="3.2" fill="#5247e5" />
          <rect x="-2.2" y="-25" width="4.4" height="4.4" rx="0.6" fill="#5247e5" />
          <rect x="-1.8" y="-24.6" width="1.4" height="1.2" rx="0.3" fill="#fafaf9" fillOpacity="0.32" />
        </g>
      </defs>
      <g transform="translate(32 32)">
        <use href="#hm-spike-sb" />
        <use href="#hm-spike-sb" transform="rotate(30)" />
        <use href="#hm-spike-sb" transform="rotate(60)" />
        <use href="#hm-spike-sb" transform="rotate(90)" />
        <use href="#hm-spike-sb" transform="rotate(120)" />
        <use href="#hm-spike-sb" transform="rotate(150)" />
        <use href="#hm-spike-sb" transform="rotate(180)" />
        <use href="#hm-spike-sb" transform="rotate(210)" />
        <use href="#hm-spike-sb" transform="rotate(240)" />
        <use href="#hm-spike-sb" transform="rotate(270)" />
        <use href="#hm-spike-sb" transform="rotate(300)" />
        <use href="#hm-spike-sb" transform="rotate(330)" />
      </g>
      <circle cx="32" cy="32" r="19" fill="url(#hm-env-sb)" />
      <circle cx="32" cy="32" r="19" fill="none" stroke="#312e81" strokeOpacity="0.55" strokeWidth="0.8" />
      <circle cx="32" cy="32" r="16" fill="none" stroke="#fafaf9" strokeOpacity="0.18" strokeWidth="0.7" />
      <g fill="none" stroke="#fafaf9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M 24.5 27 q 4 -3 8 0 q 4 3 -1 5 q -5 1 -7 -5" strokeOpacity="0.62" strokeWidth="1.5" />
        <path d="M 33 36 q 3.5 -2.2 6 0 q 1.8 2.4 -2 3.4 q -3.6 0.5 -4 -3.4" strokeOpacity="0.55" strokeWidth="1.3" />
        <path d="M 27 38 q 2.4 -1.6 4 0 q 1.2 1.6 -1 2.2 q -2.4 0.4 -3 -2.2" strokeOpacity="0.5" strokeWidth="1.2" />
      </g>
    </svg>
  );
}

function formatBig(n: number): string {
  if (n >= 100_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return n.toLocaleString();
}
